import { PropsWithChildren } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Text } from "~/components/text";
import {
  OrganisationProvider,
  useOrganisation,
} from "~/contexts/organisation-provider";
import { Tabs, TabsList } from "~/components/ui/tabs";

const OrganisationPageLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation("settings");
  const { organisation, members, id } = useOrganisation();
  const params = { organisationId: id };

  const tabLinkProps = {
    className:
      "inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-all relative",
    activeProps: {
      className:
        "text-primary-500 after:absolute after:h-0.5 after:bg-primary-500 after:bottom-0 after:left-0 after:right-0",
    },
    inactiveProps: { className: "text-secondary-400" },
  };

  return (
    <div className="py-10 space-y-8">
      <div className="px-28 border-b">
        <div className="space-y-1 mb-4">
          <Text as="h1" variant="heading">
            {organisation?.name ?? `\u00A0`}
          </Text>
          <Text variant="subtle">
            {t("settings:organisation.overview.header.members", {
              count: members.length,
            })}
          </Text>
        </div>

        <div className="-mb-1">
          <Tabs>
            <TabsList className="h-auto rounded-none border-b-0 bg-transparent px-0 pt-0 pb-1 group-data-[orientation=horizontal]/tabs:h-auto">
              <Link
                to="/settings/organisations/$organisationId/settings"
                params={params}
                {...tabLinkProps}
              >
                {t("settings:organisation.layout.tabs.settings")}
              </Link>

              <Link
                to="/settings/organisations/$organisationId/members"
                params={params}
                {...tabLinkProps}
              >
                {t("settings:organisation.layout.tabs.members")}
              </Link>

              <Link
                to="/settings/organisations/$organisationId/onix-upload"
                params={params}
                data-testid="onix-upload-tab"
                {...tabLinkProps}
              >
                {t("settings:organisation.layout.tabs.onix-upload")}
              </Link>

              <Link
                to="/settings/organisations/$organisationId/exports"
                params={params}
                {...tabLinkProps}
              >
                {t("settings:organisation.layout.tabs.exports")}
              </Link>

              <Link
                to="/settings/organisations/$organisationId/status"
                params={params}
                {...tabLinkProps}
              >
                {t("settings:organisation.layout.tabs.status")}
              </Link>

              <Link
                to="/settings/organisations/$organisationId/manuscript-processing"
                params={params}
                data-testid="manuscript-processing-tab"
                {...tabLinkProps}
              >
                {t("settings:organisation.layout.tabs.manuscript-processing")}
              </Link>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="px-28">{children}</div>
    </div>
  );
};

export function OrganisationLayout({
  organisationId,
  children,
}: PropsWithChildren<{ organisationId: string }>) {
  return (
    <OrganisationProvider id={organisationId}>
      <OrganisationPageLayout>{children}</OrganisationPageLayout>
    </OrganisationProvider>
  );
}
